import { fromDisplayKmh } from '../constants.ts';
import type { RaceState } from '../race.ts';
import type { BoatState, Vec2 } from '../types.ts';
import { candidateDestinations } from './candidates.ts';
import { raceKey, raceProgress, turnsToGo, type CourseModel } from './course.ts';
import { fastStep, isLegalClick, type FleetHull, type StepContext } from './fastStep.ts';
import type { BotLevelKnobs } from './levels.ts';

/** Turns charged to a line that reaches water with no clean click left. */
export const DEAD_END_TURNS = 3;

/**
 * Default for `BotLevelKnobs.predictedContactTurns`: on this bot's second move a rival's forecast
 * spot is solid, and nothing after. In mixed Sequential races (6 maps, both seat orders) any
 * charge on the third move cost about 0.4 of a place; a solid second move caused the fewest
 * paid hits and edged ahead. The click being made always meets the real hulls exactly.
 */
export const PREDICTED_CONTACT_TURNS: readonly number[] = [Infinity];

const DEDUPE_POSITION_PX = 20;
const DEDUPE_HEADING = (3 * Math.PI) / 180;
const DEDUPE_SPEED_PX = 10;

type Node = {
  boat: BoatState;
  race: RaceState;
  /** Turns sailed from the root boat, fractional on the finishing move. */
  turns: number;
  /** Turns charged for sailing through predicted rivals. */
  penalty: number;
  /** Estimated turns for the whole line: sailed plus to go. */
  score: number;
  depth: number;
  root: number;
  dest: Vec2;
  parent: Node | null;
  finished: boolean;
  /** Index into the warm line this node continues, or -1. */
  warm: number;
};

export type RootPlan = {
  destination: Vec2;
  /** Estimated turns to finish through this click. */
  score: number;
  /** Deepest lookahead a line from this click survived (Infinity once it finishes). */
  depth: number;
  /** The best line through this click crosses the finish inside the lookahead. */
  finished: boolean;
  /** Jobs the first move clears, as the fast model scored it. */
  jobs: number;
  /** Clicks of the best line from this root, the root's own first. */
  line: Vec2[];
};

export type PlanInput = {
  ctx: StepContext;
  model: CourseModel;
  knobs: BotLevelKnobs;
  /** Px/turn² the hull can add and shed, for the to-go estimate. */
  accelPx: number;
  brakePx: number;
  /** Hulls parked where they are now. The first click must miss them. */
  discs?: readonly FleetHull[];
  /** Where each rival is predicted to sit during this bot's later moves; index 0 is move 2. */
  predicted?: readonly (readonly FleetHull[])[];
  /**
   * When given, first clicks that pass through `discs` are collected here instead of dropped,
   * so the caller can try them against the real collision sweep.
   */
  touching?: Vec2[];
  /** Last turn's chosen line after the click it already sailed. */
  warm?: readonly Vec2[];
};

function dedupeKey(node: Node): string {
  const { position, heading, speed, drive } = node.boat;

  return [
    Math.round(position.x / DEDUPE_POSITION_PX),
    Math.round(position.y / DEDUPE_POSITION_PX),
    Math.round(heading / DEDUPE_HEADING),
    Math.round(speed / DEDUPE_SPEED_PX),
    drive ?? -1,
    raceKey(node.race),
  ].join('|');
}

/** Finished beats unfinished; then the deeper line; then the lower score. */
function better(a: { finished: boolean; depth: number; score: number }, b: typeof a): boolean {
  if (a.finished !== b.finished) {
    return a.finished;
  }

  if (!a.finished && a.depth !== b.depth) {
    return a.depth > b.depth;
  }

  return a.score < b.score;
}

function lineOf(node: Node): Vec2[] {
  const line: Vec2[] = [];

  for (let at: Node | null = node; at; at = at.parent) {
    line.push(at.dest);
  }

  return line.reverse();
}

/**
 * Receding-horizon beam search over the fast model. Every line is scored by turns sailed plus
 * turns to go; a click is ranked by the best line through it. The work is a fixed count of
 * steps, not a clock, so the same request always picks the same click.
 */
export function planRoots(boat: BoatState, race: RaceState, input: PlanInput): RootPlan[] {
  const { ctx, model, knobs, accelPx, brakePx } = input;
  const course = model.course;
  const warm = input.warm ?? [];
  const contactTurns = knobs.predictedContactTurns ?? PREDICTED_CONTACT_TURNS;
  const limitPx = (knobs.speedLimitKmh ?? 0) > 0 ? fromDisplayKmh(knobs.speedLimitKmh!) : Infinity;
  const best: { node: Node; finished: boolean; depth: number; score: number }[] = [];

  const offer = (node: Node, score: number) => {
    const entry = { node, finished: node.finished, depth: node.finished ? Infinity : node.depth, score };
    const current = best[node.root];

    if (!current || better(entry, current)) {
      best[node.root] = entry;
    }
  };

  const make = (
    parent: Node | null,
    from: BoatState,
    fromRace: RaceState,
    dest: Vec2,
    root: number,
    warmIndex: number,
  ): Node | null => {
    const depth = (parent?.depth ?? 0) + 1;
    const ahead = depth >= 2 ? input.predicted?.[depth - 2] : undefined;
    const step = fastStep(ctx, from, fromRace, dest, parent ? (ahead ?? []) : input.discs);

    // Over its own speed limit only when the boat was already faster and is not speeding up.
    if (!step || step.boat.speed > Math.max(limitPx, from.speed)) {
      return null;
    }

    if (step.touched && !parent) {
      input.touching?.push(dest);
      return null;
    }

    const charge = step.touched ? (contactTurns[depth - 2] ?? 0) : 0;

    // An infinite charge makes the predicted hull solid: that line is gone, and a line with no
    // way past it is a dead end.
    if (charge === Infinity) {
      return null;
    }

    const sailed = (parent?.turns ?? 0) + (step.finish ?? 1);
    const finished = step.finish !== null;
    const penalty = (parent?.penalty ?? 0) + charge;

    return {
      boat: step.boat,
      race: step.race,
      turns: sailed,
      penalty,
      score:
        penalty +
        (finished ? sailed : sailed + turnsToGo(model, step.boat, step.race, accelPx, brakePx, knobs)),
      depth,
      root,
      dest,
      parent,
      finished,
      warm: warmIndex,
    };
  };

  const rootDests = candidateDestinations(boat, ctx.map, ctx.feel, knobs.rootDensity);
  let warmRoot = -1;

  if (warm.length > 0 && isLegalClick(ctx.map, ctx.feel, boat, warm[0]!)) {
    warmRoot = rootDests.length;
    rootDests.push(warm[0]!);
  }

  let frontier: Node[] = [];

  rootDests.forEach((dest, root) => {
    const node = make(null, boat, race, dest, root, root === warmRoot ? 0 : -1);

    if (node) {
      offer(node, node.score);

      if (!node.finished) {
        frontier.push(node);
      }
    }
  });

  frontier = select(frontier, knobs.width);

  for (let depth = 2; depth <= knobs.depth && frontier.length > 0; depth += 1) {
    const next: Node[] = [];

    for (const node of frontier) {
      const dests = candidateDestinations(node.boat, ctx.map, ctx.feel, knobs.density);
      const warmNext = node.warm >= 0 ? node.warm + 1 : -1;
      const warmDest = warmNext >= 0 ? warm[warmNext] : undefined;

      if (warmDest && isLegalClick(ctx.map, ctx.feel, node.boat, warmDest)) {
        dests.push(warmDest);
      }

      let alive = 0;

      for (const dest of dests) {
        const child = make(node, node.boat, node.race, dest, node.root, dest === warmDest ? warmNext : -1);

        if (!child) {
          continue;
        }

        alive += 1;
        offer(child, child.score);

        if (!child.finished) {
          next.push(child);
        }
      }

      if (alive === 0) {
        offer(node, node.score + DEAD_END_TURNS);
      }
    }

    frontier = select(next, knobs.width);
  }

  const plans: RootPlan[] = [];

  best.forEach((entry, root) => {
    if (!entry) {
      return;
    }

    const line = lineOf(entry.node);
    let first: Node = entry.node;

    while (first.parent) {
      first = first.parent;
    }

    plans.push({
      destination: rootDests[root]!,
      score: entry.score,
      depth: entry.depth,
      finished: entry.finished,
      jobs: raceProgress(course, first.race),
      line,
    });
  });

  return plans.sort((a, b) => (better(a, b) ? -1 : better(b, a) ? 1 : 0));
}

/** Best `width` lines by score, one per dedupe key; the warm line is always kept. */
function select(nodes: Node[], width: number): Node[] {
  nodes.sort((a, b) => a.score - b.score);
  const seen = new Set<string>();
  const kept: Node[] = [];
  let warmKept = false;

  for (const node of nodes) {
    const key = dedupeKey(node);

    if (seen.has(key)) {
      continue;
    }

    if (kept.length < width) {
      seen.add(key);
      kept.push(node);
      warmKept ||= node.warm >= 0;
    } else if (!warmKept && node.warm >= 0) {
      seen.add(key);
      kept.push(node);
      warmKept = true;
    }
  }

  return kept;
}
